/**
 * BoardTopBar — fixed-feel header rendered above the board.
 *
 * Left:  Exit button → /playtest/solo, "Round N" label, "Player N's Turn"
 *        outline pill, "X Aggressor" red-outline pill.
 * Right: "Log" button with a small badge dot showing log count.
 *
 * The Pass button used to live here but was relocated to the centered combat
 * dock so it sits where the player's eyes already are during an exchange.
 */
import { ArrowLeft, ScrollText, Bug } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

interface BoardTopBarProps {
  round: number;
  activePlayerName: string;
  isAggressor: boolean;
  logCount: number;
  onToggleLog: () => void;
  logOpen: boolean;
  /** When true, renders the engine-trace bug icon. Admin-only — gated by
   * the parent so non-admin players never see this debugging affordance. */
  showTraceButton?: boolean;
  /** Toggle the EngineTracePanel drawer. Required when showTraceButton. */
  onToggleTrace?: () => void;
  /** Whether the EngineTracePanel is currently open (drives button styling). */
  traceOpen?: boolean;
}

export function BoardTopBar({
  round,
  activePlayerName,
  isAggressor,
  logCount,
  onToggleLog,
  logOpen,
  showTraceButton = false,
  onToggleTrace,
  traceOpen = false,
}: BoardTopBarProps) {
  return (
    <div
      className="fixed inset-x-0 top-0 z-30 flex flex-wrap items-center gap-2 border-b border-[#2a2f3a] bg-[#0e1014] px-3 py-1.5 text-xs"
      style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.375rem)" }}
    >
      <Link
        to="/playtest/solo"
        className="flex items-center gap-1 rounded px-2 py-1 text-[#94a3b8] hover:bg-[#22262f] hover:text-[#e2e8f0]"
      >
        <ArrowLeft size={12} />
        Exit
      </Link>

      <span className="text-[#94a3b8]">Round {round}</span>

      <span className="rounded border border-white/30 px-2 py-0.5 text-[10px] text-[#e2e8f0]">
        {activePlayerName}'s Turn
      </span>

      {isAggressor && (
        <span className="rounded border border-red-500/40 px-2 py-0.5 text-[10px] font-semibold text-red-400">
          ⚔ Aggressor
        </span>
      )}

      <div className="ml-auto flex items-center gap-1">
        {showTraceButton && onToggleTrace && (
          <button
            type="button"
            onClick={onToggleTrace}
            className={cn(
              "flex items-center gap-1 rounded px-2 py-1 hover:bg-[#22262f]",
              traceOpen ? "text-amber-soft" : "text-[#94a3b8] hover:text-[#e2e8f0]",
            )}
            title="Engine Trace (admin only)"
            data-testid="button-toggle-trace"
          >
            <Bug size={13} />
          </button>
        )}
        <button
          type="button"
          onClick={onToggleLog}
          className={cn(
            "relative flex items-center gap-1 rounded px-2 py-1 hover:bg-[#22262f]",
            logOpen ? "text-amber-soft" : "text-[#94a3b8] hover:text-[#e2e8f0]",
          )}
        >
          <ScrollText size={13} />
          Log
          {logCount > 0 && !logOpen && (
            <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-soft text-[8px] font-bold text-black">
              {logCount > 9 ? "9+" : logCount}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
